import React, { useState, useCallback } from "react";
import { Input } from "../design/input";
import { Field, Label, FieldGroup } from "../design/fieldset";
import { Button } from "../design/button";
import PlaceAutocomplete from "./PlaceAutocomplete";
import { Address } from "@visitproof/types";
import { MapPinIcon, PencilSquareIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { Text } from "../design/text";

interface AddressSectionProps {
  address: Address | undefined;
  onChange: (address: Address | undefined) => void;
  disabled?: boolean;
}

type InputMode = "search" | "manual";

const AddressSection: React.FC<AddressSectionProps> = ({
  address,
  onChange,
  disabled = false,
}) => {
  const [inputMode, setInputMode] = useState<InputMode>("search");
  const [manualAddress, setManualAddress] = useState<Address>({
    streetAddress: address?.streetAddress ?? "",
    streetAddress2: address?.streetAddress2 ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    zipCode: address?.zipCode ?? "",
  });

  // Parse address components from Google Places result
  const parseAddressComponents = (
    addressComponents: google.maps.GeocoderAddressComponent[]
  ): Partial<Address> => {
    const result: Partial<Address> = {};

    for (const component of addressComponents) {
      const types = component.types;

      if (types.includes("street_number")) {
        result.streetAddress = component.long_name;
      } else if (types.includes("route")) {
        result.streetAddress = result.streetAddress
          ? `${result.streetAddress} ${component.long_name}`
          : component.long_name;
      } else if (types.includes("subpremise")) {
        result.streetAddress2 = component.long_name;
      } else if (types.includes("locality")) {
        result.city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        result.state = component.short_name;
      } else if (types.includes("postal_code")) {
        result.zipCode = component.long_name;
      } else if (types.includes("country")) {
        result.country = component.short_name;
      }
    }

    return result;
  };

  const handlePlaceSelect = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      if (!place) return;

      const parsedAddress = place.address_components
        ? parseAddressComponents(place.address_components)
        : {};

      const newAddress: Address = {
        ...parsedAddress,
        formattedAddress: place.formatted_address ?? "",
        coordinates: place.geometry?.location
          ? {
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            }
          : undefined,
      };

      setManualAddress(newAddress);
      onChange(newAddress);
    },
    [onChange]
  );

  const handleManualChange = (field: keyof Address, value: string) => {
    const updated = { ...manualAddress, [field]: value };

    // Update formatted address for manual entries
    const parts = [
      updated.streetAddress,
      updated.streetAddress2,
      updated.city,
      updated.state,
      updated.zipCode,
    ].filter(Boolean);
    updated.formattedAddress = parts.join(", ");

    setManualAddress(updated);
    onChange(updated);
  };

  const handleClearAddress = () => {
    setManualAddress({
      streetAddress: "",
      streetAddress2: "",
      city: "",
      state: "",
      zipCode: "",
    });
    onChange(undefined);
  };

  const hasAddress = Boolean(
    address?.streetAddress || address?.formattedAddress
  );

  const tabOptions = [
    { key: "search" as const, label: "Search", icon: MagnifyingGlassIcon },
    { key: "manual" as const, label: "Manual Entry", icon: PencilSquareIcon },
  ];

  return (
    <div className="space-y-4">
      {/* Current Address Display */}
      {hasAddress && (
        <div className="p-3 border rounded-lg border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2">
              <MapPinIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-zinc-500" />
              <div className="flex flex-col gap-0.5">
                {address?.streetAddress && (
                  <Text className="text-sm text-zinc-900 dark:text-zinc-100">
                    {address.streetAddress}
                  </Text>
                )}
                {address?.streetAddress2 && (
                  <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                    {address.streetAddress2}
                  </Text>
                )}
                {(address?.city || address?.state || address?.zipCode) && (
                  <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                    {[address.city, address.state, address.zipCode]
                      .filter(Boolean)
                      .join(", ")}
                  </Text>
                )}
              </div>
            </div>
            <Button
              plain
              onClick={handleClearAddress}
              disabled={disabled}
              className="text-sm"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Input Mode Tabs */}
      <div className="flex p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
        {tabOptions.map((tab) => {
          const isActive = inputMode === tab.key;
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setInputMode(tab.key)}
              disabled={disabled}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100"
                  : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
              } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
              <TabIcon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Google Search Input */}
      {inputMode === "search" && (
        <Field>
          <Label>Search for an address</Label>
          <PlaceAutocomplete
            onPlaceSelect={handlePlaceSelect}
            placeholder="Start typing to search..."
          />
          <Text className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
            Search for an address using Google Maps
          </Text>
        </Field>
      )}

      {/* Manual Entry Form */}
      {inputMode === "manual" && (
        <FieldGroup>
          <Field>
            <Label>Street Address</Label>
            <Input
              type="text"
              value={manualAddress.streetAddress ?? ""}
              placeholder="123 Main Street"
              onChange={(e) =>
                handleManualChange("streetAddress", e.target.value)
              }
              disabled={disabled}
            />
          </Field>
          <Field>
            <Label>
              Apt, Suite, etc.{" "}
              <span className="text-zinc-400">(optional)</span>
            </Label>
            <Input
              type="text"
              value={manualAddress.streetAddress2 ?? ""}
              placeholder="Apt 4B"
              onChange={(e) =>
                handleManualChange("streetAddress2", e.target.value)
              }
              disabled={disabled}
            />
          </Field>
          <div className="grid grid-cols-6 gap-4">
            <Field className="col-span-3">
              <Label>City</Label>
              <Input
                type="text"
                value={manualAddress.city ?? ""}
                placeholder="Los Angeles"
                onChange={(e) => handleManualChange("city", e.target.value)}
                disabled={disabled}
              />
            </Field>
            <Field className="col-span-1">
              <Label>State</Label>
              <Input
                type="text"
                value={manualAddress.state ?? ""}
                placeholder="CA"
                maxLength={2}
                onChange={(e) =>
                  handleManualChange("state", e.target.value.toUpperCase())
                }
                disabled={disabled}
              />
            </Field>
            <Field className="col-span-2">
              <Label>ZIP Code</Label>
              <Input
                type="text"
                value={manualAddress.zipCode ?? ""}
                placeholder="90001"
                onChange={(e) => handleManualChange("zipCode", e.target.value)}
                disabled={disabled}
              />
            </Field>
          </div>
        </FieldGroup>
      )}
    </div>
  );
};

export default AddressSection;
